import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, TypographyTagVariants } from '@components/atoms/Typography';
import { PopOver } from '@components/atoms/PopOver';
import { OrderPoints } from '../OrderPoints/OrderPoints';
import { ProductsList } from '../ProductsList/ProductsList';
import { formatPrice } from '@utils/format';
import styles from '../ShoppingCartTotal.module.css';

export interface OrderSummaryProps {
    showPointsBelowTotalOnMobile?: boolean;
    productsValue: number;
    discount: number;
    eligibleItems: string[];
    subtotal: number;
    shipping: number | null;
    points: number;
    products: { name: string; image: string; price: number; quantity: number }[];
}

export const OrderSummary: FC<OrderSummaryProps> = ({
    showPointsBelowTotalOnMobile,
    productsValue,
    discount,
    eligibleItems,
    subtotal,
    shipping,
    points,
    products,
}) => {
    const { t } = useTranslation();

    const translations = {
        valueLabel: t('shopping-cart-total-component.order-summary.products-value-label'),
        subtotalLabel: t('shopping-cart-total-component.order-summary.subtotal-label'),
        shippingLabel: t('shopping-cart-total-component.order-summary.shipping-label'),
        discountLabel: t('shopping-cart-total-component.discount.label'),
        discountPopoverTitle: t('shopping-cart-total-component.discount.popover.title', { count: eligibleItems.length }),
    };

    return (
        <>
            {products.length > 0 && (
                <div className={`${styles.indent} border-bottom pb-2`}>
                    <ProductsList products={products} />
                </div>
            )}
            <div className={`${styles.indent} pt-4 mt-2`}>
                {/* products total value */}
                <div className="d-flex justify-content-between" data-testid="products-value">
                    <Typography classes="mb-0" fontSize="16">
                        {translations.valueLabel}
                    </Typography>
                    <Typography classes="mb-0" fontSize="16">
                        {formatPrice(productsValue)}
                    </Typography>
                </div>
                {/* discount value */}
                {discount !== 0 && (
                    <>
                        <hr className="my-2" />
                        <div className="d-flex justify-content-between" data-testid="discount">
                            <Typography classes="mb-0" fontSize="16" color="danger-dark" fontWeight="800">
                                <>
                                    {translations.discountLabel}
                                    <Typography classes="ms-2" color="muted" tagVariant={TypographyTagVariants.span}>
                                        <PopOver
                                            key={eligibleItems.join(',')}
                                            title={translations.discountPopoverTitle}
                                            text={eligibleItems.join('<br/>')}
                                            placement="bottom">
                                            <i className="fa-light fa-circle-info"></i>
                                        </PopOver>
                                    </Typography>
                                </>
                            </Typography>

                            <Typography classes="mb-0" fontSize="16" color="danger-dark" fontWeight="800">
                                {formatPrice(discount)}
                            </Typography>
                        </div>
                    </>
                )}
                {/* shipping value */}
                {shipping !== null && (
                    <>
                        <hr className="my-2" />
                        <div className="d-flex justify-content-between" data-testid="shipping">
                            <Typography classes="mb-0" fontSize="16">
                                {translations.shippingLabel}
                            </Typography>
                            <Typography classes="mb-0" fontSize="16">
                                {formatPrice(shipping)}
                            </Typography>
                        </div>
                    </>
                )}
                <hr className="my-2" />
                {/* subtotal value */}
                <div className="d-flex justify-content-between" data-testid="subtotal">
                    <Typography classes="mb-0" fontSize="16">
                        {translations.subtotalLabel}
                    </Typography>
                    <Typography classes="mb-0" fontSize="16">
                        {formatPrice(subtotal)}
                    </Typography>
                </div>
                <hr className={`${showPointsBelowTotalOnMobile !== true && points !== 0 ? 'd-none d-lg-block' : ''} mt-2 mb-1`} />
            </div>
            {/* points - visible in smaller screens only */}
            {showPointsBelowTotalOnMobile !== true && (
                <OrderPoints classes="border-top border-bottom d-lg-none mt-2" points={points} />
            )}
        </>
    );
};
